/**
 * Finance > Invoices > vendor popup.
 *
 * A read-only modal that shows the canonical ERP vendor record matched to an
 * invoice. Given {erp, no}, it fetches the vendor via useVendorRecord and
 * renders the vendor card: canonical name, ERP, vendor number, address, email,
 * phone, terms, account, status. Loading + error states inline. Backdrop click
 * and Escape both close. Read-only for now (no edit affordance).
 *
 * Mirrors the modal pattern in shell/Header.tsx (SignOutModal) and
 * commerce/MatchCustomerModal.tsx: fixed backdrop, centered card, stopPropagation
 * on the card, Escape-to-dismiss.
 */

import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Pill } from '@/components/Pill';
import {
  useVendorRecord,
  vendorErpLabel,
  type VendorErp,
  type VendorMatch,
  type VendorMatchFields,
  type VendorRecord,
} from '@/lib/useInvoices';

type Props = {
  erp: VendorErp;
  vendorNo: string;
  onClose: () => void;
};

export function VendorPopup({ erp, vendorNo, onClose }: Props) {
  const vendorQ = useVendorRecord(erp, vendorNo);

  // Escape to dismiss; backdrop click also dismisses (handled on the wrapper).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="vendor-popup-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-lg border border-amp-line bg-amp-white p-6 shadow-xl dark:border-amp-dark-line dark:bg-amp-dark-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>

        {vendorQ.isLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading vendor...
          </div>
        ) : vendorQ.error ? (
          <div className="py-2">
            <h2
              id="vendor-popup-title"
              className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-2"
            >
              Vendor
            </h2>
            <p className="text-sm text-red-700 dark:text-red-300">
              Could not load this vendor ({vendorQ.error.message}).
            </p>
          </div>
        ) : vendorQ.data ? (
          <VendorCard vendor={vendorQ.data} />
        ) : null}
      </div>
    </div>
  );
}

// A subtle marker distinguishing an exact match from a fuzzy one. Exact gets a
// neutral ERP tag only; fuzzy adds a muted "~ fuzzy" pill so a reviewer knows
// the name was matched approximately and may want to confirm it.
function MatchKindMarker({ match }: { match: VendorMatch | null }) {
  if (match === 'fuzzy') {
    return (
      <Pill
        title="Fuzzy match: vendor matched approximately"
        tone="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
        className="shrink-0"
      >
        ~ fuzzy
      </Pill>
    );
  }
  return null;
}

// Hover tooltip for a matched vendor name, e.g. "Match: 92% (fuzzy)" or
// "Match: 100% (exact)". Falls back to the match kind alone when the backend
// did not send a confidence score.
function matchTooltip(
  score: number | null,
  match: VendorMatch | null,
): string {
  const kind = match ?? 'matched';
  if (score == null) return `Match: ${kind}`;
  return `Match: ${Math.round(score)}% (${kind})`;
}

/**
 * The Vendor cell / field shared by the Invoices list and the invoice detail.
 *
 * - Matched: renders the canonical (matched) vendor name as a button that opens
 *   the read-only VendorPopup, with a muted "~ fuzzy" pill when the match was
 *   approximate. The match confidence shows on hover as a title tooltip. The
 *   ERP source is shown separately (the Source column in the list, the source
 *   label on the detail), not inline here.
 * - Unmatched: renders the AI-extracted vendor name in muted ink, with no pill
 *   and no link. The Source column conveys the unmatched state (it shows "--").
 *
 * Both states truncate to a single line with the full value in a title tooltip,
 * so the Vendor column holds a fixed width and the grid does not side-scroll.
 */
export function MatchedVendor({
  fields,
  extractedVendor,
}: {
  fields: VendorMatchFields;
  extractedVendor: string | null;
}) {
  const [open, setOpen] = useState(false);
  const {
    matched_vendor_erp,
    matched_vendor_no,
    matched_vendor_name,
    vendor_match,
    vendor_match_score,
  } = fields;

  const matched =
    !!matched_vendor_erp && !!matched_vendor_no && !!matched_vendor_name;

  if (!matched) {
    const name = extractedVendor ?? '--';
    return (
      <span
        className="block truncate text-amp-ink-muted dark:text-amp-dark-ink-muted"
        title={extractedVendor ?? undefined}
      >
        {name}
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1.5 min-w-0">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen(true);
        }}
        title={matchTooltip(vendor_match_score, vendor_match)}
        className="min-w-0 truncate text-amp-olive hover:underline text-left dark:text-amp-olive-dark"
      >
        {matched_vendor_name}
      </button>
      <MatchKindMarker match={vendor_match} />
      {open && matched_vendor_erp && matched_vendor_no ? (
        <VendorPopup
          erp={matched_vendor_erp}
          vendorNo={matched_vendor_no}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </span>
  );
}

/**
 * The matched-vendor ERP source, shown as its own labeled Pill. Used by the
 * Source column in the Invoices list and the matched-vendor row on the detail.
 * Renders "Sage" / "MS BC" / "Ramp" for a matched vendor, or a muted "--" when
 * the invoice did not match any ERP vendor.
 */
export function VendorSource({ erp }: { erp: VendorErp | null }) {
  if (!erp) {
    return <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">--</span>;
  }
  return (
    <Pill tone="bg-amp-cream text-amp-ink dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink">
      {vendorErpLabel(erp)}
    </Pill>
  );
}

function VendorCard({ vendor }: { vendor: VendorRecord }) {
  const lines = [
    vendor.address1,
    vendor.address2,
    [vendor.city, vendor.state, vendor.postal].filter(Boolean).join(' '),
    vendor.country,
  ].filter((l) => l && l.trim() !== '');

  return (
    <>
      <div className="mb-4 pr-6">
        <div className="flex items-center gap-2 mb-1">
          <Pill tone="bg-amp-cream text-amp-ink dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink">
            {vendorErpLabel(vendor.erp)}
          </Pill>
          <span className="font-mono text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {vendor.vendor_no}
          </span>
        </div>
        <h2
          id="vendor-popup-title"
          className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink break-words"
        >
          {vendor.vendor_name ?? vendor.vendor_no}
        </h2>
      </div>

      <dl className="space-y-2 text-sm">
        {lines.length > 0 && (
          <VendorRow label="Address">
            <div className="space-y-0.5">
              {lines.map((l, i) => (
                <div key={i}>{l}</div>
              ))}
            </div>
          </VendorRow>
        )}
        <VendorRow label="Email">
          {vendor.email ? (
            <a
              href={`mailto:${vendor.email}`}
              className="text-amp-olive hover:underline dark:text-amp-olive-dark break-all"
            >
              {vendor.email}
            </a>
          ) : (
            '--'
          )}
        </VendorRow>
        <VendorRow label="Phone">{vendor.phone ?? '--'}</VendorRow>
        <VendorRow label="Terms">{vendor.terms ?? '--'}</VendorRow>
        <VendorRow label="GL Code">{vendor.account ?? '--'}</VendorRow>
        <VendorRow label="Status">{vendor.status ?? '--'}</VendorRow>
      </dl>
    </>
  );
}

function VendorRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 text-amp-ink-muted dark:text-amp-dark-ink-muted">{label}</dt>
      <dd className="flex-1 text-amp-ink dark:text-amp-dark-ink break-words">{children}</dd>
    </div>
  );
}
